import * as React from "react";

import { rawPalette } from "@impero-com/ui/styles/theme.css";
import { cx } from "class-variance-authority";
import Color from "color";
import { ResponsiveContainer } from "recharts";

import * as theme from "./theme.css";

// Default width of the SVG
const DEFAULT_WIDTH = 800;
// Default height of the SVG
const DEFAULT_HEIGHT = 500;

// Default thickness of the x/y axis lines
const DEFAULT_AXIS_LINE_WIDTH = 2;

// Default thickness of the tick
const DEFAULT_TICK_WIDTH = 1;
const DEFAULT_TICK_LENGTH = 18;

// Default space between the top/end of the label in the x/y axis
const DEFAULT_LABEL_MARGIN = 4;

const DEFAULT_GRADIENT_STOPS = [
    { color: "#d4eacf", percentOffset: 0 },
    { color: "#d4eacf", percentOffset: 20 },
    { color: "#faf0ca", percentOffset: 50 },
    { color: "#fbcdcd", percentOffset: 75 },
    { color: "#fbcdcd", percentOffset: 100 },
];

// The gradient is the top right quadrant of an ellipse, but it may extend
// beyond the box
// The ratio determines how large both axes are compared to the size of the box
const DEFAULT_GRADIENT_ELLIPSE_RATIO = 1.5;

const DEFAULT_GRID_LINE_WIDTH = 1;

const DEFAULT_CIRCLE_RADIUS = 30;

/**
 * The position of the various elements of the table
 */
interface Layout {
    xAxisPosition: Position;
    yAxisPosition: Position;
    tablePosition: Position;
}

/**
 * The position of an element within the SVG. Contains:
 * - topLeft: the coordinates of the top left corner of the element.
 * - outerWidth: the outer width of the element as computed by computeLayout()
 * - outerHeight: the outer height of the element as computed by computeLayout()
 */
interface Position {
    topLeft: GlobalXY;
    outerWidth: number;
    outerHeight: number;
}

/**
 * Coordinates local to an element, where {localX: 0, localY: 0} is the top
 * left of the element.
 */
interface LocalXY {
    localX: number;
    localY: number;
}

/**
 * Coordinates global to the SVG, where {globalX: 0, globalY: 0} is the top
 * left of the SVG.
 */
interface GlobalXY {
    globalX: number;
    globalY: number;
}

/**
 * Prunes the list of handlers, and if there is at least one then calls them
 * like bubbles. Handlers are called in the order of the list.
 * It returns an undefined meta-handler if there are no handlers.
 */
function buildBubblingEventHandler<E extends React.SyntheticEvent<any>>(
    handlers: Array<React.EventHandler<E> | undefined> | undefined
): React.EventHandler<E> | undefined {
    const prunedHandlers = handlers
        ? (handlers.filter((h) => (h ? true : false)) as Array<
              React.EventHandler<E>
          >)
        : undefined;
    if (prunedHandlers && prunedHandlers.length > 0) {
        return (event) => {
            // TODO implement a way to stop bubbles
            for (const prunedHandler of prunedHandlers) {
                prunedHandler(event);
            }
        };
    } else {
        return undefined;
    }
}

/**
 * Extends an event and calls the extended handler.
 * It returns an undefined meta-handler if there are no handlers.
 */
function buildExtendedEventHandler<
    E extends React.SyntheticEvent<any>,
    F extends React.SyntheticEvent<any>,
>(
    extendEvent: (event: E) => F | undefined,
    handler?: React.EventHandler<F>
): React.EventHandler<E> | undefined {
    if (handler) {
        return (event) => {
            const extendedEvent: F | undefined = extendEvent(event);
            if (extendedEvent) {
                handler(extendedEvent);
            }
        };
    } else {
        return undefined;
    }
}

/**
 * Cell mouse event which contains heatmap coordinates.
 */
interface HeatmapCellMouseEvent<T> extends React.MouseEvent<T> {
    heatmapX: string;
    heatmapY: string;
}

/**
 * Event handler extender for heatmap coordinates.
 */
function buildHeatmapCellMouseEventHandler<T>(
    x: string,
    y: string,
    handler: React.EventHandler<HeatmapCellMouseEvent<T>> | undefined
): React.MouseEventHandler<T> | undefined {
    return buildExtendedEventHandler(
        (event) => Object.assign(event, { heatmapX: x, heatmapY: y }),
        buildBubblingEventHandler([handler])
    );
}

/**
 * Given the width and height of the SVG, as well as the width of the x axis
 * line (to extend the y axis slightly), computes the layout of the SVG.
 */
function computeLayout(
    outerWidth: number,
    outerHeight: number,
    xAxisLineWidth: number
): Layout {
    // 4 different areas:
    // - the table itself (unknown width and height, depends on outerWidth/outerHeight)
    // - the X axis with its caption (unknown width, fixed height)
    // - the Y axis with its caption (fixed width, unknown height)
    // All offsets are relative to the top left corner which has the coords (0, 0)

    // unknown xAxisOuterWidth
    const xAxisOuterHeight = 56;

    const yAxisOuterWidth = 100;
    // unknown yAxisOuterHeight

    const tableOuterWidth = outerWidth - yAxisOuterWidth;
    const tableOuterHeight = outerHeight - xAxisOuterHeight;
    if (tableOuterWidth <= 0 || tableOuterHeight <= 0) {
        throw new Error(
            `Illegal dimensions (too small): width=${outerWidth}, height=${outerHeight}, width of y axis=${yAxisOuterWidth}, height of x axis=${xAxisOuterHeight}`
        );
    }

    const xAxisOuterWidth = tableOuterWidth;
    const yAxisOuterHeight = tableOuterHeight + xAxisLineWidth; // Extend the line of the y axis to reach the bottom of the line of the X axis, otherwise they only have the corner of 2 pixels touching

    // Now we have the height and width of each area
    // The xAxis starts at the edge of the yAxis, below the table
    const xAxisPosition = {
        topLeft: {
            globalX: yAxisOuterWidth,
            globalY: tableOuterHeight,
        },
        outerWidth: xAxisOuterWidth,
        outerHeight: xAxisOuterHeight,
    };
    // The yAxis starts at the top left corner
    const yAxisPosition = {
        topLeft: {
            globalX: 0,
            globalY: 0,
        },
        outerWidth: yAxisOuterWidth,
        outerHeight: yAxisOuterHeight,
    };

    // The table starts after the yAxis, and at the top
    const tablePosition = {
        topLeft: {
            globalX: yAxisOuterWidth,
            globalY: 0,
        },
        outerWidth: tableOuterWidth,
        outerHeight: tableOuterHeight,
    };

    return {
        xAxisPosition,
        yAxisPosition,
        tablePosition,
    };
}

/**
 * Displays a heatmap (an SVG table with a gradient).
 */
class InnerTableHeatmap<T> extends React.Component<TableHeatmapProps<T>> {
    private svg = React.createRef<SVGSVGElement>();
    render() {
        const {
            data,
            getCellData,
            getShadowCellData,
            width,
            height,
            xAxis,
            yAxis,
            gradient,
            gridLineWidth,
            radius,
        } = this.props;
        const { onCellClick, onCircleClick } = this.props;
        const { onCellMouseMove, onCircleMouseMove } = this.props;
        if (xAxis.values.length === 0) {
            throw new Error("Need at least one value in xAxis.values");
        }
        if (yAxis.values.length === 0) {
            throw new Error("Need at least one value in yAxis.values");
        }
        const xAxisLineWidth =
            xAxis.lineWidth !== undefined
                ? xAxis.lineWidth
                : DEFAULT_AXIS_LINE_WIDTH;
        const actualWidth = width !== undefined ? width : DEFAULT_WIDTH;
        const actualHeight = height !== undefined ? height : DEFAULT_HEIGHT;
        const layout = computeLayout(actualWidth, actualHeight, xAxisLineWidth);
        const actualGradient = gradient
            ? gradient
            : {
                  stops: DEFAULT_GRADIENT_STOPS,
                  ellipseRatio: DEFAULT_GRADIENT_ELLIPSE_RATIO,
              };
        const gradientId = "ImperoHeatmapGradient";
        const CirclesElt: React.FC<CirclesProps<T>> = Circles;

        const extendGradientMouseEvent = <T extends object>(
            event: React.MouseEvent<T>
        ) => {
            const tableCoords = this.getTableCoordinates(
                event.clientX,
                event.clientY,
                layout
            );
            if (tableCoords) {
                return Object.assign(event, {
                    heatmapX: tableCoords.x,
                    heatmapY: tableCoords.y,
                });
            } else {
                return undefined;
            }
        };

        // The translation in the gradient moves it to the bottom left corner
        return (
            <svg
                ref={this.svg}
                viewBox={`0 0 ${actualWidth} ${actualHeight}`}
                width={actualWidth}
                height={actualHeight}
            >
                <defs>
                    <radialGradient
                        id={gradientId}
                        cx="50%"
                        cy="50%"
                        r={`${actualGradient.ellipseRatio * 100}%`}
                        gradientTransform="translate(-0.5 0.5)"
                    >
                        {actualGradient.stops.map((stop, i) => {
                            return (
                                <stop
                                    key={i}
                                    offset={`${stop.percentOffset}%`}
                                    stopColor={stop.color}
                                />
                            );
                        })}
                    </radialGradient>
                </defs>
                <XAxis
                    position={layout.xAxisPosition}
                    values={xAxis.values}
                    caption={xAxis.caption}
                    labelMarginTop={
                        xAxis.labelMargin !== undefined
                            ? xAxis.labelMargin
                            : DEFAULT_LABEL_MARGIN
                    }
                    lineWidth={xAxisLineWidth}
                    tickWidth={
                        xAxis.tickWidth !== undefined
                            ? xAxis.tickWidth
                            : DEFAULT_TICK_WIDTH
                    }
                    tickLength={
                        xAxis.tickLength !== undefined
                            ? xAxis.tickLength
                            : DEFAULT_TICK_LENGTH
                    }
                />
                <YAxis
                    position={layout.yAxisPosition}
                    values={yAxis.values}
                    caption={yAxis.caption}
                    labelMarginRight={
                        yAxis.labelMargin !== undefined
                            ? yAxis.labelMargin
                            : DEFAULT_LABEL_MARGIN
                    }
                    lineWidth={
                        yAxis.lineWidth !== undefined
                            ? yAxis.lineWidth
                            : DEFAULT_AXIS_LINE_WIDTH
                    }
                    tickWidth={
                        yAxis.tickWidth !== undefined
                            ? yAxis.tickWidth
                            : DEFAULT_TICK_WIDTH
                    }
                    tickLength={
                        yAxis.tickLength !== undefined
                            ? yAxis.tickLength
                            : DEFAULT_TICK_LENGTH
                    }
                    linePadding={xAxisLineWidth}
                />
                <GradientArea
                    gradientId={gradientId}
                    position={layout.tablePosition}
                    onClick={buildExtendedEventHandler(
                        extendGradientMouseEvent,
                        onCellClick
                    )}
                    onMouseMove={buildExtendedEventHandler(
                        extendGradientMouseEvent,
                        onCellMouseMove
                    )}
                >
                    <Grid
                        position={layout.tablePosition}
                        xValues={xAxis.values}
                        yValues={yAxis.values}
                        lineWidth={
                            gridLineWidth !== undefined
                                ? gridLineWidth
                                : DEFAULT_GRID_LINE_WIDTH
                        }
                    >
                        <CirclesElt
                            position={layout.tablePosition}
                            data={data}
                            getCellData={getCellData}
                            getShadowCellData={getShadowCellData}
                            xValues={xAxis.values}
                            yValues={yAxis.values}
                            radius={
                                radius !== undefined
                                    ? radius
                                    : DEFAULT_CIRCLE_RADIUS
                            }
                            gradient={actualGradient}
                            onCircleClick={buildBubblingEventHandler([
                                onCircleClick,
                                onCellClick,
                            ])}
                            onCircleMouseMove={buildBubblingEventHandler([
                                onCircleMouseMove,
                                onCellMouseMove,
                            ])}
                        />
                    </Grid>
                </GradientArea>
            </svg>
        );
    }
    private getTableCoordinates(
        clientX: number,
        clientY: number,
        layout: Layout
    ) {
        if (!this.svg.current) {
            return undefined;
        }
        const { xAxis, yAxis } = this.props;
        const pt = this.svg.current.createSVGPoint();
        pt.x = clientX;
        pt.y = clientY;
        const cursorPt = pt.matrixTransform(
            this.svg.current.getScreenCTM()!.inverse()
        );
        const svgX = cursorPt.x - layout.tablePosition.topLeft.globalX;
        const svgY = cursorPt.y - layout.tablePosition.topLeft.globalY;
        const xIndex = Math.floor(
            (svgX * xAxis.values.length) / layout.tablePosition.outerWidth
        );
        const yIndex =
            yAxis.values.length -
            Math.floor(
                (svgY * yAxis.values.length) / layout.tablePosition.outerHeight
            ) -
            1;
        if (
            xIndex >= 0 &&
            xIndex < xAxis.values.length &&
            yIndex >= 0 &&
            yIndex < yAxis.values.length
        ) {
            return {
                x: xAxis.values[xIndex],
                y: yAxis.values[yIndex],
            };
        } else {
            return undefined;
        }
    }
}

interface TableHeatmapProps<T> {
    data: T;
    getCellData: (
        data: T,
        x: string,
        y: string
    ) => { value: number; diffValue: number | null } | undefined;
    getShadowCellData: (
        data: T,
        x: string,
        y: string
    ) => { value: number; diffValue: number | null } | undefined;
    xAxis: AxisProps;
    yAxis: AxisProps;
    height?: number;
    width?: number;
    gradient?: Gradient;
    gridLineWidth?: number;
    bottomLeftGradientColor?: string;
    topRightGradientColor?: string;
    radius?: number;
    circleGradientStops?: Array<string>;
    // Bubbling events
    onCellClick?: React.EventHandler<HeatmapCellMouseEvent<SVGElement>>;
    onCircleClick?: React.EventHandler<HeatmapCellMouseEvent<SVGElement>>;
    onCellMouseMove?: React.EventHandler<HeatmapCellMouseEvent<SVGElement>>;
    onCircleMouseMove?: React.EventHandler<HeatmapCellMouseEvent<SVGElement>>;
}

interface Stop {
    color: string;
    percentOffset: number;
}

interface Gradient {
    stops: Array<Stop>;
    ellipseRatio: number;
}

interface AxisProps {
    values: Array<string>;
    caption: string;
    captionColor?: string;
    labelColor?: string;
    labelMargin?: number;
    lineWidth?: number;
    lineColor?: number;
    tickWidth?: number;
    tickLength?: number;
    tickColor?: string;
}

/**
 * The horizontal axis under the table itself.
 * Contains, from top to bottom:
 * - 1 line (of thickness lineWidth)
 * - one label per value and one 1 tick after each value
 * - 1 caption underneath the labels
 */
function XAxis(props: XAxisProps) {
    const {
        position,
        values,
        caption,
        labelMarginTop,
        lineWidth,
        tickWidth,
        tickLength,
    } = props;

    // A function to convert local coordinates into "global" (from the top of the SVG) coordinates
    const toGlobalXY = buildXYConverter(position);

    // Constants
    const captionHeight = 16;
    const labelTextHeight = 16; // Approximate height of the label in pixels

    // Line
    const localLineX = 0;
    const localLineY = 0; // Line starts at the top left corner
    const globalLineXY = toGlobalXY({ localX: localLineX, localY: localLineY });
    const lineLength = position.outerWidth;

    // Labels
    // The axis has one label per item in the "values" array
    // It also has one tick per item of width tickWidth

    const totalLabelWidth = position.outerWidth - values.length * tickWidth;
    if (totalLabelWidth < 0) {
        throw new Error(
            "Not enough horizontal space for the labels on the X axis"
        );
    }
    const labelHeight = position.outerHeight - lineWidth - captionHeight;
    if (labelHeight < 0) {
        throw new Error(
            "Not enough vertical space for the labels on the X axis"
        );
    }
    if (labelHeight < labelMarginTop) {
        throw new Error(
            "Not enough vertical space for the margin of the labels on the X axis"
        );
    }
    const labelWidth = Math.floor(totalLabelWidth / values.length);
    // We can precompute the Y coordinate of the labels since it does not change
    const localLabelY = lineWidth + labelMarginTop + labelTextHeight; // The anchor is at the bottom of the text box, that's why it's not labelTextHeight/2

    // Ticks
    // Same for the ticks
    const localTickY = lineWidth; // Tick at the top, under the line

    // Caption
    const localCaptionX = position.outerWidth / 2; // The caption block horizontally centered
    const localCaptionY = lineWidth + labelHeight + captionHeight; // Centered on the remaining space below the labels
    const globalCaptionXY = toGlobalXY({
        localX: localCaptionX,
        localY: localCaptionY,
    });

    return (
        <g>
            <rect
                className={theme.xLine}
                x={globalLineXY.globalX}
                y={globalLineXY.globalY}
                width={lineLength}
                height={lineWidth}
            />
            {values.map((value, index) => {
                // Each element advances by a fixed step, so the offset is
                // derived from the index (no render-time mutation).
                const localLeftOffset = index * (labelWidth + tickWidth);
                // The text is anchored in the middle,
                // so the 'x' coordinate is the center of the label
                const localLabelX =
                    localLeftOffset + Math.floor(labelWidth / 2);
                const localTickX = localLeftOffset + labelWidth;
                const globalLabelXY = toGlobalXY({
                    localX: localLabelX,
                    localY: localLabelY,
                });
                const globalTickXY = toGlobalXY({
                    localX: localTickX,
                    localY: localTickY,
                });
                return [
                    <text
                        className={theme.xLabel}
                        key={`label_${index}`}
                        x={globalLabelXY.globalX}
                        y={globalLabelXY.globalY}
                        textAnchor="middle"
                    >
                        {value}
                    </text>,
                    <rect
                        className={theme.xTick}
                        key={`tick_${index}`}
                        x={globalTickXY.globalX}
                        y={globalTickXY.globalY}
                        width={tickWidth}
                        height={tickLength}
                    />,
                ];
            })}
            <text
                className={theme.xCaption}
                x={globalCaptionXY.globalX}
                y={globalCaptionXY.globalY}
                textAnchor="middle"
            >
                {caption}
            </text>
        </g>
    );
}

interface XAxisProps {
    values: Array<string>;
    labelMarginTop: number;
    caption: string;
    position: Position;
    lineWidth: number;
    tickWidth: number;
    tickLength: number;
}

function reverseArray<T>(arr: Array<T>): Array<T> {
    if (arr.length === 0) {
        return [];
    }
    const revertedArray: Array<T> = [];
    for (let i = arr.length - 1; i >= 0; i--) {
        revertedArray.push(arr[i]);
    }
    return revertedArray;
}

/**
 * The vertical axis on the left of the table.
 *
 * Content, from left to right:
 * - 1 caption (with vertical text)
 * - one label per value, one tick before each label
 * - one vertical line
 */
function YAxis(props: YAxisProps) {
    const {
        position,
        values,
        caption,
        labelMarginRight,
        lineWidth,
        tickWidth,
        tickLength,
        linePadding,
    } = props;

    // The vertical line needs to extend by linePadding pixels (included in the outerHeight computation), but the rest of the content needs to stay in a box of width position.outerWidth and height position.outerHeight - linePadding
    // Compute an "inner" width and height for the normal content
    const innerWidth = position.outerWidth; // No change
    const innerHeight = position.outerHeight - linePadding;

    // A function to convert local coordinates into "global" (from the top of the SVG) coordinates
    const toGlobalXY = buildXYConverter(position);

    // Constants
    const captionWidth = 16; // The caption has a vertical text

    // Captions - the anchor is at the middle bottom of the unrotated text (maybe?)
    const localCaptionX = innerHeight / 2;
    const localCaptionY = captionWidth;
    // Don't use toGlobalXY, we do not want to perform checks here
    const globalCaptionXY = {
        globalX: localCaptionX + position.topLeft.globalX,
        globalY: localCaptionY + position.topLeft.globalY,
    };

    // Labels
    // The axis has one label per item in the "values" array
    // It also has (values.length - 1) ticks of width tickWidth
    const totalLabelHeight = innerHeight - values.length * tickWidth;
    if (totalLabelHeight < 0) {
        throw new Error(
            "Not enough vertical space for the labels on the Y axis"
        );
    }
    const labelWidth = innerWidth - captionWidth - labelMarginRight - lineWidth;
    if (labelWidth < 0) {
        throw new Error(
            "Not enough horizontal space for the labels on the Y axis"
        );
    }
    if (labelWidth < labelMarginRight) {
        throw new Error(
            "Not enough horizontal space for the margin of the labels on the Y axis"
        );
    }
    const labelHeight = Math.floor(totalLabelHeight / values.length);

    const localLabelX = captionWidth + labelWidth; // The anchor is at the bottom right of the text box

    // Tick
    const localTickX = innerWidth - lineWidth - tickLength; // Tick at the left of the line

    // Line
    const lineLength = position.outerHeight; // Don't use the inner height here
    const localLineX = innerWidth - lineWidth;
    const localLineY = 0; // Line starts at the top, lineWidth pixels to the right border
    const globalLineXY = toGlobalXY({ localX: localLineX, localY: localLineY });

    // It's easier to reason if we start laying out labels from the top, so we need to start at the last value
    const revertedValues = reverseArray(values);
    return (
        <g>
            <text
                className={theme.yCaption}
                x={globalCaptionXY.globalX * -1}
                y={globalCaptionXY.globalY}
                textAnchor="middle"
                transform="rotate(270)"
            >
                {caption}
            </text>
            {revertedValues.map((value, index) => {
                // Each element advances by a fixed step, so the offset is
                // derived from the index (no render-time mutation).
                const localTopOffset = index * (labelHeight + tickWidth);
                const localTickY = localTopOffset;
                // The text is anchored in the middle,
                // so the 'x' coordinate is the center of the label
                const localLabelY =
                    localTopOffset + tickWidth + Math.floor(labelHeight / 2);
                const globalLabelXY = toGlobalXY({
                    localX: localLabelX,
                    localY: localLabelY,
                });
                const globalTickXY = toGlobalXY({
                    localX: localTickX,
                    localY: localTickY,
                });
                return [
                    <rect
                        className={theme.yTick}
                        key={`tick_${index}`}
                        x={globalTickXY.globalX}
                        y={globalTickXY.globalY}
                        width={tickLength}
                        height={tickWidth}
                    />,
                    <text
                        className={theme.yLabel}
                        key={`label_${index}`}
                        x={globalLabelXY.globalX}
                        y={globalLabelXY.globalY}
                        textAnchor="end"
                    >
                        {value}
                    </text>,
                ];
            })}
            <rect
                className={theme.yLine}
                x={globalLineXY.globalX}
                y={globalLineXY.globalY}
                width={lineWidth}
                height={lineLength}
            />
        </g>
    );
}

interface YAxisProps {
    values: Array<string>;
    labelMarginRight: number;
    caption: string;
    position: Position;
    lineWidth: number;
    tickWidth: number;
    tickLength: number;
    linePadding: number;
}

/**
 * An area with a gradient. The gradient must be defined elsewhere in the SVG.
 */
function GradientArea(props: GradientAreaProps) {
    const { position, gradientId, children } = props;
    const { onClick, onMouseMove } = props;
    // A function to convert local coordinates into "global" (from the top of the SVG) coordinates
    const toGlobalXY = buildXYConverter(position);
    const globalXY = toGlobalXY({ localX: 0, localY: 0 });
    return (
        <g>
            <rect
                className={`${onClick ? theme.clickable : ""}`}
                x={globalXY.globalX}
                y={globalXY.globalY}
                width={position.outerWidth}
                height={position.outerHeight}
                fill={`url(#${gradientId})`}
                onClick={onClick}
                onMouseMove={onMouseMove}
            />
            {children}
        </g>
    );
}

interface GradientAreaProps {
    gradientId: string;
    position: Position;
    children?: React.ReactNode;
    // Bubbling events
    onClick?: React.MouseEventHandler<SVGElement>;
    onMouseMove?: React.MouseEventHandler<SVGElement>;
}

/**
 * The grid displayed over the gradient.
 *
 * Content: empty cells (one per xValues.length * yValues.length) + horizontal
 * and vertical lines of width lineWidth after each cell, so yValues.length
 * horizontal lines and xValues.length vertical lines
 */
function Grid(props: GridProps) {
    const { position, xValues, yValues, lineWidth, children } = props;

    // A function to convert local coordinates into "global" (from the top of the SVG) coordinates
    const toGlobalXY = buildXYConverter(position);

    // Cell
    const horizontalLineCount = yValues.length;
    const verticalLineCount = xValues.length;
    const totalVerticalLineWidth = verticalLineCount * lineWidth;
    const totalHorizontalLineHeight = horizontalLineCount * lineWidth;
    const totalCellWidth = position.outerWidth - totalVerticalLineWidth;
    const totalCellHeight = position.outerHeight - totalHorizontalLineHeight;
    const cellWidth = Math.floor(totalCellWidth / xValues.length);
    const cellHeight = Math.floor(totalCellHeight / yValues.length);

    return (
        <g>
            {xValues.map((_, index) => {
                // First vertical line starts at x=cellWidth, then a fixed step
                // per element — derived from the index (no render-time mutation).
                const localLineX = cellWidth + index * (lineWidth + cellWidth);
                const localLineY = 0; // Line starts at the top
                const globalLineXY = toGlobalXY({
                    localX: localLineX,
                    localY: localLineY,
                });
                const lineLength = position.outerHeight;
                return (
                    <rect
                        key={index}
                        className={theme.gridLine}
                        x={globalLineXY.globalX}
                        y={globalLineXY.globalY}
                        width={lineWidth}
                        height={lineLength}
                    />
                );
            })}
            {yValues.map((_, index) => {
                const localLineX = 0; // Line starts at the left
                // First horizontal line starts at the top, then a fixed step
                // per element — derived from the index (no render-time mutation).
                const localLineY = index * (lineWidth + cellHeight);
                const globalLineXY = toGlobalXY({
                    localX: localLineX,
                    localY: localLineY,
                });
                const lineLength = position.outerWidth;
                return (
                    <rect
                        key={index}
                        className={theme.gridLine}
                        x={globalLineXY.globalX}
                        y={globalLineXY.globalY}
                        width={lineLength}
                        height={lineWidth}
                    />
                );
            })}
            {children}
        </g>
    );
}

interface GridProps {
    position: Position;
    xValues: Array<string>;
    yValues: Array<string>;
    lineWidth: number;
    children?: React.ReactNode;
}

// Circle Component
interface CircleProps {
    xValue: string;
    yValue: string;
    cx: number;
    cy: number;
    r: number;
    className: string;
    fill: string;
    onLocalClick?: any;
    onLocalMouseMove?: any;
    additionalProps?: { [key: string]: any };
}

const Circle: React.FC<CircleProps> = ({
    xValue,
    yValue,
    cx,
    cy,
    r,
    className,
    fill,
    onLocalClick,
    onLocalMouseMove,
    additionalProps = {},
}) => (
    <circle
        key={`circle-${xValue}-${yValue}`}
        className={`${className} ${onLocalClick ? theme.clickable : ""}`}
        cx={cx}
        cy={cy}
        r={r}
        fill={fill}
        onClick={onLocalClick}
        onMouseMove={onLocalMouseMove}
        {...additionalProps}
    />
);

// Text Component
interface TextProps {
    xValue: string;
    yValue: string;
    x: number;
    y: number;
    textClassName: string;
    onLocalClick?: React.MouseEventHandler<SVGElement>;
    onLocalMouseMove?: React.MouseEventHandler<SVGGElement>;
    content: React.ReactNode;
    additionalProps?: { [key: string]: any };
}

const Text: React.FC<TextProps> = ({
    xValue,
    yValue,
    x,
    y,
    textClassName,
    onLocalClick,
    onLocalMouseMove,
    content,
    additionalProps = {},
}) => {
    return (
        <text
            key={`text-${xValue}-${yValue}`}
            className={`${textClassName} ${
                onLocalClick ? theme.clickable : ""
            }`}
            x={x}
            y={y}
            textAnchor="middle"
            onClick={onLocalClick}
            onMouseMove={onLocalMouseMove}
            {...additionalProps}
        >
            {content}
        </text>
    );
};

// DiffCircleAndText Component
interface DiffCircleAndTextProps {
    xValue: string;
    yValue: string;
    globalCenterXY: GlobalXY;
    circleRadius: number;
    delta: number;
    diffOffsetX: number;
    diffOffsetY: number;
    cellType: { diff: boolean; shadow: boolean };
    cellData: { value: number; diffValue: number | null };
    onLocalClick?: any;
    onLocalMouseMove?: any;
    additionalCircleProps?: { [key: string]: any };
    additionalTextProps?: { [key: string]: any };
}

const DiffCircleAndText: React.FC<DiffCircleAndTextProps> = ({
    xValue,
    yValue,
    globalCenterXY,
    circleRadius,
    delta,
    diffOffsetX,
    diffOffsetY,
    cellType,
    cellData,
    onLocalClick,
    onLocalMouseMove,
    additionalCircleProps = {},
    additionalTextProps = {},
}) => {
    const diffCircleRadius = circleRadius / 2;
    const diffCenterX =
        globalCenterXY.globalX +
        diffOffsetX +
        (cellType.diff && !cellType.shadow ? -delta : delta);
    const diffCenterY =
        globalCenterXY.globalY +
        diffOffsetY +
        (cellType.diff && !cellType.shadow ? delta : -delta);

    return (
        <>
            <Circle
                xValue={xValue}
                yValue={yValue}
                cx={diffCenterX}
                cy={diffCenterY}
                r={diffCircleRadius}
                className={cx(theme.diffCircle, {
                    [theme.clickable]: onLocalClick,
                })}
                fill={rawPalette.white}
                onLocalClick={onLocalClick}
                onLocalMouseMove={onLocalMouseMove}
                additionalProps={{
                    key: `circle-${xValue}-${yValue}`,
                    ...additionalCircleProps,
                }}
            />
            <text
                key={`diff-text-${xValue}-${yValue}`}
                className={cx(
                    theme.diffCircleLabel,
                    cellType.diff && cellType.shadow
                        ? theme.diffCircleLabelShadow
                        : "",
                    { [theme.clickable]: onLocalClick }
                )}
                x={diffCenterX}
                y={diffCenterY}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize="smaller"
                onClick={onLocalClick}
                onMouseMove={onLocalMouseMove}
                {...additionalTextProps}
            >
                {cellData.diffValue! > 0 ? "+" : ""}
                {cellData.diffValue}
            </text>
        </>
    );
};

function Circles<T>(props: CirclesProps<T>) {
    const {
        position,
        data,
        getCellData,
        getShadowCellData,
        xValues,
        yValues,
        radius,
        gradient,
    } = props;
    const { onCircleClick, onCircleMouseMove } = props;

    // Constants
    const labelHeight = 33;
    const smallLabelHeight = 24;
    const smallRadius = radius / 2;

    // A function to convert local coordinates into "global" (from the top of the SVG) coordinates
    const toGlobalXY = buildXYConverter(position);

    const cellWidth = position.outerWidth / xValues.length;
    const cellHeight = position.outerHeight / yValues.length;
    // It's easier to reason if we start laying out values from the top, so we need to start at the last value
    const revertedYValues = reverseArray(yValues);

    // The radiuses of the gradient ellipse
    const ellipseRX = position.outerWidth * gradient.ellipseRatio;
    const ellipseRY = position.outerHeight * gradient.ellipseRatio;

    return (
        <g>
            {xValues.map((xValue, xIndex) => {
                return revertedYValues.map((yValue, yIndex) => {
                    // Calculate positions based on indices rather than accumulating offsets
                    const localCenterX = (xIndex + 0.5) * cellWidth;
                    const localCenterY = (yIndex + 0.5) * cellHeight;

                    // Remember, we're operating on an inverted version of
                    // yValues, so we need to translate yIndex into the SC
                    // coordinates system
                    const cellData = getCellData(data, xValue, yValue);
                    const shadowCellData = getShadowCellData(
                        data,
                        xValue,
                        yValue
                    );

                    if (
                        cellData === undefined &&
                        shadowCellData === undefined
                    ) {
                        return null;
                    }

                    const scCenterX = localCenterX;
                    const scCenterY = position.outerHeight - localCenterY;
                    const lengthToCellCenter = Math.sqrt(
                        Math.pow(scCenterX, 2) + Math.pow(scCenterY, 2)
                    );

                    // theta = angle between x axis at the bottom and
                    // line from the bottom left corner (center of the
                    // gradient ellipse) to the center of the cell
                    const cosTheta = scCenterX / lengthToCellCenter;
                    const sinTheta = scCenterY / lengthToCellCenter;

                    // Find the distance of a line from the center of
                    // the ellipse to the edge of the ellipse
                    const lengthToEllipseEdge =
                        (ellipseRX * ellipseRY) /
                        Math.sqrt(
                            Math.pow(sinTheta * ellipseRX, 2) +
                                Math.pow(cosTheta * ellipseRY, 2)
                        );

                    const colorPercentage = Math.floor(
                        (lengthToCellCenter / lengthToEllipseEdge) * 100
                    );

                    const color = findColor(gradient.stops, colorPercentage);

                    const globalCenterXY = toGlobalXY({
                        localX: localCenterX,
                        localY: localCenterY,
                    });

                    // Event handlers
                    const onLocalClick = buildHeatmapCellMouseEventHandler(
                        xValue,
                        yValue,
                        onCircleClick
                    );

                    const onLocalMouseMove = buildHeatmapCellMouseEventHandler(
                        xValue,
                        yValue,
                        onCircleMouseMove
                    );

                    // Create a unique key for this cell
                    const cellKey = `cell-${xValue}-${yValue}`;

                    return (
                        <React.Fragment key={cellKey}>
                            {cellData && (
                                <>
                                    <Circle
                                        xValue={xValue}
                                        yValue={yValue}
                                        cx={globalCenterXY.globalX}
                                        cy={globalCenterXY.globalY}
                                        r={radius}
                                        className={theme.circle}
                                        fill={color}
                                        onLocalClick={onLocalClick}
                                        onLocalMouseMove={onLocalMouseMove}
                                    />
                                    <Text
                                        xValue={xValue}
                                        yValue={yValue}
                                        x={globalCenterXY.globalX}
                                        y={
                                            globalCenterXY.globalY +
                                            labelHeight / 4
                                        }
                                        textClassName={theme.circleLabel}
                                        onLocalClick={onLocalClick}
                                        onLocalMouseMove={onLocalMouseMove}
                                        content={cellData.value}
                                    />
                                    {cellData.diffValue !== null &&
                                        cellData.diffValue !== 0 && (
                                            <DiffCircleAndText
                                                xValue={xValue}
                                                yValue={yValue}
                                                globalCenterXY={globalCenterXY}
                                                circleRadius={radius}
                                                delta={0}
                                                diffOffsetX={radius * 0.8}
                                                diffOffsetY={-radius * 0.8}
                                                cellType={{
                                                    diff: true,
                                                    shadow: false,
                                                }}
                                                cellData={cellData}
                                                onLocalClick={onLocalClick}
                                                onLocalMouseMove={
                                                    onLocalMouseMove
                                                }
                                                additionalCircleProps={{
                                                    filter: "drop-shadow( 0px 1px 2px rgba(0, 0, 0, .3))",
                                                }}
                                            />
                                        )}
                                </>
                            )}

                            {shadowCellData && (
                                <>
                                    <Circle
                                        xValue={xValue}
                                        yValue={yValue}
                                        cx={
                                            globalCenterXY.globalX +
                                            (cellData ? 40 : 0)
                                        }
                                        cy={
                                            globalCenterXY.globalY -
                                            (cellData ? 40 : 0)
                                        }
                                        r={cellData ? smallRadius : radius}
                                        className={
                                            cellData
                                                ? theme.smallShadowCircle
                                                : theme.shadowCircle
                                        }
                                        fill={color}
                                        onLocalClick={onLocalClick}
                                        onLocalMouseMove={onLocalMouseMove}
                                    />
                                    <Text
                                        xValue={xValue}
                                        yValue={yValue}
                                        x={
                                            globalCenterXY.globalX +
                                            (cellData ? 40 : 0)
                                        }
                                        y={
                                            globalCenterXY.globalY -
                                            (cellData ? 40 : 0) +
                                            (cellData
                                                ? smallLabelHeight
                                                : labelHeight) /
                                                4
                                        }
                                        textClassName={
                                            cellData
                                                ? theme.smallShadowCircleLabel
                                                : theme.shadowCircleLabel
                                        }
                                        onLocalClick={onLocalClick}
                                        onLocalMouseMove={onLocalMouseMove}
                                        content={shadowCellData.value}
                                    />
                                    {shadowCellData.diffValue !== null &&
                                        shadowCellData.diffValue !== 0 && (
                                            <DiffCircleAndText
                                                xValue={xValue}
                                                yValue={yValue}
                                                globalCenterXY={globalCenterXY}
                                                circleRadius={
                                                    cellData
                                                        ? smallRadius
                                                        : radius
                                                }
                                                delta={cellData ? 40 : 0}
                                                diffOffsetX={
                                                    (cellData
                                                        ? smallRadius
                                                        : radius) * 0.6
                                                }
                                                diffOffsetY={
                                                    -(cellData
                                                        ? smallRadius
                                                        : radius) * 0.6
                                                }
                                                cellType={{
                                                    diff: true,
                                                    shadow: true,
                                                }}
                                                cellData={shadowCellData}
                                                onLocalClick={onLocalClick}
                                                onLocalMouseMove={
                                                    onLocalMouseMove
                                                }
                                                additionalCircleProps={{
                                                    opacity: 0.5,
                                                }}
                                            />
                                        )}
                                </>
                            )}
                        </React.Fragment>
                    );
                });
            })}
        </g>
    );
}

interface CirclesProps<T> {
    position: Position;
    data: T;
    getCellData: (
        data: T,
        x: string,
        y: string
    ) => { value: number; diffValue: number | null } | undefined;
    getShadowCellData: (
        data: T,
        x: string,
        y: string
    ) => { value: number; diffValue: number | null } | undefined;
    xValues: Array<string>;
    yValues: Array<string>;
    radius: number;
    gradient: Gradient;
    // Bubbling events
    onCircleClick?: React.EventHandler<HeatmapCellMouseEvent<SVGElement>>;
    onCircleMouseMove?: React.EventHandler<HeatmapCellMouseEvent<SVGElement>>;
}

function circleColor(baseColor: Color): string {
    const finalColor = Color()
        .hue(baseColor.hue())
        .saturationl(baseColor.saturationl())
        .lightness(baseColor.lightness() * 0.8)
        .alpha(1);
    return finalColor.hex();
}

function findColor(stops: Array<Stop>, percentage: number): string {
    if (stops.length === 0) {
        throw new Error("No stops");
    }

    if (percentage <= stops[0].percentOffset) {
        return circleColor(Color(stops[0].color));
    }

    if (stops[stops.length - 1].percentOffset <= percentage) {
        return circleColor(Color(stops[stops.length - 1].color));
    }

    let lowerBoundStop: Stop | undefined;
    let upperBoundStop: Stop | undefined;
    for (let i = 1; i < stops.length; i++) {
        const previousStop = stops[i - 1];
        const currentStop = stops[i];
        if (previousStop.percentOffset >= percentage) {
            throw new Error("Array of stops not sorted");
        }
        if (currentStop.percentOffset === percentage) {
            return circleColor(Color(currentStop.color));
        }
        if (
            previousStop.percentOffset < percentage &&
            percentage < currentStop.percentOffset
        ) {
            lowerBoundStop = previousStop;
            upperBoundStop = currentStop;
            break;
        }
    }
    if (!lowerBoundStop || !upperBoundStop) {
        throw new Error("Couldn't find the right stop in the array");
    }
    // We have a lower bound and an upper bound, find out the ratio of the percentage
    const ratio =
        (percentage - lowerBoundStop.percentOffset) /
        (upperBoundStop.percentOffset - lowerBoundStop.percentOffset);
    const startColor = Color(lowerBoundStop.color);
    const endColor = Color(upperBoundStop.color);
    const deltaHue = (endColor.hue() - startColor.hue()) * ratio;
    const deltaSaturation =
        (endColor.saturationl() - startColor.saturationl()) * ratio;
    const deltaLightness =
        (endColor.lightness() - startColor.lightness()) * ratio;
    const deltaAlpha = (endColor.alpha() - startColor.alpha()) * ratio;
    let hue = startColor.hue() + deltaHue;
    let saturation = startColor.saturationl() + deltaSaturation;
    let lightness = startColor.lightness() + deltaLightness;
    let alpha = startColor.alpha() + deltaAlpha;
    if (hue > 360) {
        hue -= 360;
    } else if (hue < 0) {
        hue += 360;
    }
    if (saturation > 100) {
        saturation = 100;
    } else if (saturation < 0) {
        saturation = 0;
    }
    if (lightness > 100) {
        lightness = 100;
    } else if (lightness < 0) {
        lightness = 0;
    }
    if (alpha > 1) {
        alpha = 1;
    } else if (alpha < 0) {
        alpha = 0;
    }
    const percentageColor = Color()
        .hue(hue)
        .saturationl(saturation)
        .lightness(lightness)
        .alpha(alpha);
    return circleColor(percentageColor);
}

// Returns a function which, given a set of coordinates local to the widget,
// can compute the global coordinates in the SVG
function buildXYConverter(position: Position) {
    return (localXY: LocalXY) => {
        const globalX = localXY.localX + position.topLeft.globalX;
        const globalY = localXY.localY + position.topLeft.globalY;
        const maxX = position.topLeft.globalX + position.outerWidth;
        if (globalX > maxX) {
            throw new Error(`Illegal X position ${globalX} (> maxX: ${maxX})`);
        }
        const maxY = position.topLeft.globalY + position.outerHeight;
        if (globalY > maxY) {
            throw new Error(`Illegal Y position ${globalY} (> maxY: ${maxY})`);
        }
        return {
            globalX,
            globalY,
        };
    };
}

function TableHeatmap<T>(props: TableHeatmapProps<T>) {
    const { height } = props;
    const actualHeight = height !== undefined ? height : DEFAULT_HEIGHT;
    return (
        <ResponsiveContainer width="100%" height={actualHeight}>
            <InnerTableHeatmap {...props} />
        </ResponsiveContainer>
    );
}

interface DummyData {
    Likelihood: string;
    Impact: string;
    Quantity: number;
}

function DummyTableHeatmap() {
    const data: Array<DummyData> = [
        { Likelihood: "Remote", Impact: "Low", Quantity: 20 },
        { Likelihood: "Nearly certain", Impact: "Average", Quantity: 308 },
        { Likelihood: "Nearly certain", Impact: "Very high", Quantity: 308 },
    ];
    const xAxisValues = [
        "Remote",
        "Remote2",
        "Likely",
        "Likely2",
        "Nearly certain",
    ];
    const yAxisValues = ["Low", "Low2", "Average", "Average2", "Very high"];
    const TableHeatmapElt: React.FC<TableHeatmapProps<Array<DummyData>>> =
        TableHeatmap;
    const getCellData = (
        data: Array<DummyData>,
        likelihood: string,
        impact: string
    ) => {
        const item = data.find(
            (d) => d.Likelihood === likelihood && d.Impact === impact
        );
        return item !== undefined
            ? { value: Number(item.Quantity), diffValue: null }
            : undefined;
    };
    const getShadowCellData = (
        data: Array<DummyData>,
        likelihood: string,
        impact: string
    ) => {
        const item = data.find(
            (d) => d.Likelihood === likelihood && d.Impact === impact
        );
        return item !== undefined
            ? { value: Number(item.Quantity), diffValue: null }
            : undefined;
    };
    return (
        <ResponsiveContainer width="100%" height={500}>
            <TableHeatmapElt
                data={data}
                getCellData={getCellData}
                getShadowCellData={getShadowCellData}
                xAxis={{ values: xAxisValues, caption: "Likelihood" }}
                yAxis={{ values: yAxisValues, caption: "Impact" }}
            />
        </ResponsiveContainer>
    );
}

export { DummyTableHeatmap, type TableHeatmapProps, TableHeatmap };
